import { t } from '@lingui/core/macro';
import { useState } from 'react';

import { usePixelOptions } from '@/api/pixel';
import {
  encodeFilterFieldName,
  FilterField,
  InfiniteComboboxPickerFilter,
  InfiniteScrollCategoryFilter,
  useFilterForm,
} from '@/components/filters';
import type { FilterOperatorMulti } from '@/components/InstantSearch';
import type { FilterComponentProps } from '@/providers/TableState';

const PAGE_SIZE = 50;

type Props = FilterComponentProps & {
  mode?: 'multiple';
  hasExclude?: boolean;
};

const PixelFilter = ({
  name,
  label,
  infoPopover,
  compact = false,
  mode = 'multiple',
  hasExclude = true,
  ...props
}: Props) => {
  const form = useFilterForm();
  const [search, setSearch] = useState<string>('');

  const { pixelOptions, isLoading, isFetchingNextPage, error, hasMore, fetchNextPage } =
    usePixelOptions({ search, pageSize: PAGE_SIZE });

  return (
    <form.AppField name={encodeFilterFieldName(name)}>
      {(field) => (
        <FilterField
          data-testid="pixel-filter"
          label={label}
          infoPopover={infoPopover}
          hideLabel={!compact}
        >
          {compact ? (
            <InfiniteComboboxPickerFilter
              {...props}
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              placeholder={t`Select Pixels...`}
              loadingPlaceholder={t`Loading Pixels...`}
              options={pixelOptions}
              isLoading={isLoading}
              isLoadingMore={isFetchingNextPage}
              hasMore={hasMore}
              error={error}
              loadMore={fetchNextPage}
              notFoundContent={t`No pixels were found...`}
              hasExclude={hasExclude}
              onSearch={setSearch}
            />
          ) : (
            <InfiniteScrollCategoryFilter
              {...props}
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              selectionMode={mode}
              options={pixelOptions}
              isLoading={isLoading}
              isLoadingMore={isFetchingNextPage}
              hasMore={hasMore}
              error={error}
              loadMore={fetchNextPage}
              searchText={search}
              onSearch={setSearch}
            />
          )}
        </FilterField>
      )}
    </form.AppField>
  );
};

export default PixelFilter;
